import React, { useCallback, useEffect, useState } from 'react';
import {
  SparklesIcon, XMarkIcon, CheckIcon, ExclamationTriangleIcon, InformationCircleIcon, TrashIcon,
  PlusIcon, KeyIcon, CpuChipIcon, BoltIcon, ArrowPathIcon, LockClosedIcon,
} from '@heroicons/react/24/outline';
import aiConfigService from '../../services/aiConfigService';

// =============================================================================
// Settings › AI assistant — providers and models.
//
// Ported from Mantle (frontend/src/pages/Settings/AiConfigModal.jsx) to
// OmniPrac's styling and icons. Mantle's "let this provider see query results"
// switch is not here: OmniPrac never sends procedure results back to the model.
//
// THE API KEY IS WRITE-ONLY. The screen shows whether one is stored and its last
// four characters, and offers to replace it. A SAVE THAT OMITS THE KEY KEEPS IT
// — hence the deliberate "Replace" step.
// =============================================================================

const EMPTY_PROVIDER = {
  provider_code: '', display_name: '', api_style: 'anthropic', base_url: '',
  is_active: false, timeout_seconds: 60, max_output_tokens: 4096, notes: '',
};

const errOf = (e, fallback) => e?.response?.data?.message || e?.response?.data?.err || e?.message || fallback;

export default function AiConfigModal({ onClose, onChanged }) {
  const [data, setData] = useState({ providers: [], models: [], presets: [], encryption_ready: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [editing, setEditing] = useState(null);
  const [addingModelFor, setAddingModelFor] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await aiConfigService.get();
      if (res.success) setData(res.data);
      else setError(res.message || 'Could not load the AI configuration.');
    } catch (e) {
      setError(errOf(e, 'Could not load the AI configuration.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reloadAll = async () => { await load(); onChanged?.(); };
  const say = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2600); };

  const removeProvider = async (code) => {
    if (!window.confirm(`Remove ${code}?\n\nThis deletes its stored API key and every model under it. The assistant stops working until another provider is active.`)) return;
    try {
      const res = await aiConfigService.deleteProvider(code);
      if (res.success) { say(res.message); await reloadAll(); } else setError(res.message);
    } catch (e) { setError(errOf(e)); }
  };

  const runTest = async (code) => {
    setTesting(code); setTestResult(null); setError('');
    try {
      setTestResult({ code, ...(await aiConfigService.testProvider(code)) });
    } catch (e) {
      setTestResult({ code, success: false, message: errOf(e) });
    } finally { setTesting(null); }
  };

  const adoptModel = async (providerCode, m) => {
    try {
      const res = await aiConfigService.saveModel({
        provider_code: providerCode,
        model_code: m.model_code,
        display_name: m.display_name || m.model_code,
        context_tokens: m.context_tokens || null,
        is_default: !data.models.some((x) => x.is_default),
        is_active: true,
        sort_order: data.models.filter((x) => x.provider_code === providerCode).length * 10,
      });
      if (res.success) { setTestResult(null); say(`Added ${m.model_code}.`); await reloadAll(); } else setError(res.message);
    } catch (e) { setError(errOf(e)); }
  };

  const setDefault = async (m) => {
    try {
      const res = await aiConfigService.saveModel({ ...m, is_default: true });
      if (res.success) { say(`${m.display_name} is now the assistant's model.`); await reloadAll(); } else setError(res.message);
    } catch (e) { setError(errOf(e)); }
  };

  const removeModel = async (providerCode, modelCode) => {
    try {
      const res = await aiConfigService.deleteModel(providerCode, modelCode);
      if (res.success) { say('Model removed.'); await reloadAll(); } else setError(res.message);
    } catch (e) { setError(errOf(e)); }
  };

  const applyPreset = (preset) => setEditing({ ...EMPTY_PROVIDER, ...preset, _preset: preset, _isNew: true });
  const modelsOf = (code) => data.models.filter((m) => m.provider_code === code);

  return (
    <Shell title="AI assistant — provider" onClose={onClose}>
      {loading ? (
        <div className="grid h-40 place-items-center"><ArrowPathIcon className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {!data.encryption_ready && (
            <Notice tone="danger" Icon={ExclamationTriangleIcon} title="The server has no AI_ENCRYPTION_KEY.">
              API keys cannot be saved until it is set in the backend environment and the server restarted.
            </Notice>
          )}
          <Notice tone="info" Icon={InformationCircleIcon} title="What the provider receives.">
            The request text, the list of approved schedule actions, and schedule context (doctor names, hospitals, session dates and times — no patient data).
            Action results stay in OmniPrac and are never sent back to the model. Use <strong>Test</strong> after saving a key.
          </Notice>

          {error && <Notice tone="danger" Icon={ExclamationTriangleIcon} title="Something went wrong.">{error}</Notice>}
          {flash && <Notice tone="success" Icon={CheckIcon} title={flash} />}

          {data.providers.length === 0 ? (
            <p className="mb-5 text-sm text-slate-500">No AI service is connected yet. Start from a preset below.</p>
          ) : (
            <div className="mb-6 space-y-3">
              {data.providers.map((p) => (
                <div key={p.provider_code} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-start gap-3 px-4 py-3">
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      <SparklesIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-800">{p.display_name}</h3>
                        <code className="text-[10px] text-slate-400">{p.provider_code}</code>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{p.api_style}</span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="mt-0.5 break-all text-[11px] text-slate-500">{p.base_url}</p>
                      <p className="mt-0.5 text-[11px] text-slate-400">
                        {p.key_set
                          ? <>API key stored{p.api_key_hint ? <> · ends <code>{p.api_key_hint}</code></> : null}</>
                          : <span className="text-amber-600">No API key — cannot be switched on.</span>}
                        {' · '}timeout {p.timeout_seconds}s · max {Number(p.max_output_tokens).toLocaleString()} output tokens
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        onClick={() => runTest(p.provider_code)}
                        disabled={!p.key_set || testing === p.provider_code}
                        title="A tiny real request to check the endpoint, key and model"
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-brand-navy disabled:opacity-40"
                      >
                        {testing === p.provider_code ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <BoltIcon className="h-3.5 w-3.5" />} Test
                      </button>
                      <button onClick={() => setEditing({ ...p, _isNew: false })} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-brand-navy">
                        Edit
                      </button>
                      <button onClick={() => removeProvider(p.provider_code)} title={`Remove ${p.provider_code}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:border-red-300 hover:text-red-600">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {testResult?.code === p.provider_code && <TestResult result={testResult} onAdopt={(m) => adoptModel(p.provider_code, m)} />}

                  <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Models</p>
                      <button onClick={() => setAddingModelFor(p.provider_code)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-mid hover:underline">
                        <PlusIcon className="h-3.5 w-3.5" /> Add model
                      </button>
                    </div>
                    {modelsOf(p.provider_code).length === 0 ? (
                      <p className="text-[11px] text-slate-400">None yet — the assistant needs a model to call.</p>
                    ) : (
                      <ul className="space-y-1">
                        {modelsOf(p.provider_code).map((m) => (
                          <li key={m.model_code} className="flex items-center gap-2 text-[11px]">
                            <CpuChipIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            <span className="font-medium text-slate-700">{m.display_name}</span>
                            <code className="text-slate-400">{m.model_code}</code>
                            {m.is_default
                              ? <span className="rounded bg-brand-navy px-1.5 text-[10px] font-semibold text-white">used by the assistant</span>
                              : <button onClick={() => setDefault(m)} className="text-[10px] font-semibold text-brand-mid hover:underline">use this</button>}
                            {!m.is_active && <span className="text-slate-400">(inactive)</span>}
                            {m.input_price_per_1k != null && (
                              <span className="text-slate-400" title="Input / output price per 1,000 tokens">
                                {m.price_currency} {Number(m.input_price_per_1k)}/{Number(m.output_price_per_1k ?? 0)} per 1k
                              </span>
                            )}
                            <button onClick={() => removeModel(p.provider_code, m.model_code)} title={`Remove ${m.model_code}`} className="ml-auto text-slate-400 hover:text-red-600">
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-slate-200 pt-4">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Add a provider</p>
            <p className="mb-3 text-[11px] text-slate-400">
              A starting point, not a supported list — check the endpoint and model codes against the provider’s current documentation. Every field stays editable.
            </p>
            <div className="flex flex-wrap gap-2">
              {data.presets.map((preset) => (
                <button
                  key={preset.provider_code}
                  onClick={() => applyPreset(preset)}
                  disabled={data.providers.some((p) => p.provider_code === preset.provider_code)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-brand-navy disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {preset.display_name}
                </button>
              ))}
              <button onClick={() => setEditing({ ...EMPTY_PROVIDER, _isNew: true })} className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-500 hover:border-brand-navy">
                Something else…
              </button>
            </div>
          </div>
        </>
      )}

      {editing && (
        <ProviderForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={async (msg, providerCode, preset) => {
            setEditing(null);
            say(msg);
            // A brand-new provider from a preset gets its suggested models straight away.
            if (preset?.models?.length) {
              for (const [i, m] of preset.models.entries()) {
                // eslint-disable-next-line no-await-in-loop
                await aiConfigService.saveModel({ ...m, provider_code: providerCode, is_active: true, sort_order: i * 10, is_default: i === 0 && !data.models.some((x) => x.is_default) }).catch(() => {});
              }
            }
            await reloadAll();
          }}
        />
      )}

      {addingModelFor && (
        <ModelForm
          providerCode={addingModelFor}
          preset={data.presets.find((p) => p.provider_code === addingModelFor)}
          existing={data.models.filter((m) => m.provider_code === addingModelFor)}
          hasDefault={data.models.some((m) => m.is_default)}
          onCancel={() => setAddingModelFor(null)}
          onSaved={async (msg) => { setAddingModelFor(null); say(msg); await reloadAll(); }}
        />
      )}
    </Shell>
  );
}

const TestResult = ({ result, onAdopt }) => (
  <div className={`border-t px-4 py-2.5 text-[11px] ${result.success ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
    <p className="font-bold">{result.success ? 'Reached it.' : 'Could not reach it.'}</p>
    <p className="mt-0.5">{result.message}</p>
    {result.success && result.data && <p className="mt-0.5 opacity-80">Replied in {result.data.duration_ms}ms. {result.data.note}</p>}
    {!result.success && (result.endpoint || result.attempted_model) && (
      <p className="mt-1 opacity-80">
        Tried <code>{result.attempted_model}</code>{result.endpoint && <> at <code>{result.endpoint}</code></>}{result.status && <> · HTTP {result.status}</>}
      </p>
    )}
    {!result.success && result.detail && (
      <details className="mt-1">
        <summary className="cursor-pointer opacity-80">What the provider said</summary>
        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded bg-slate-900/80 p-2 text-[10px] text-slate-100">{result.detail}</pre>
      </details>
    )}
    {!result.success && result.available_models?.length > 0 && (
      <div className="mt-2">
        <p className="font-bold">This key can reach {result.available_models.length} model{result.available_models.length === 1 ? '' : 's'}. Click one to add it:</p>
        <div className="mt-1 flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
          {result.available_models.map((m) => (
            <button key={m.model_code} type="button" onClick={() => onAdopt(m)} className="rounded border border-current/30 px-2 py-1 font-mono text-[10px] hover:bg-white/60">
              {m.model_code}
            </button>
          ))}
        </div>
      </div>
    )}
  </div>
);

const ProviderForm = ({ initial, onCancel, onSaved }) => {
  const [f, setF] = useState(initial);
  const [apiKey, setApiKey] = useState('');
  const [replacing, setReplacing] = useState(!initial.key_set);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true); setProblem('');
    try {
      const payload = {
        provider_code: f.provider_code.trim().toUpperCase(),
        display_name: f.display_name,
        api_style: f.api_style,
        base_url: f.base_url,
        is_active: !!f.is_active,
        timeout_seconds: Number(f.timeout_seconds) || 60,
        max_output_tokens: Number(f.max_output_tokens) || 4096,
        notes: f.notes || '',
      };
      if (replacing && apiKey.trim()) payload.api_key = apiKey.trim();
      const res = await aiConfigService.saveProvider(payload);
      if (res.success) onSaved('Provider saved.', payload.provider_code, f._isNew ? f._preset : null);
      else setProblem(res.message || 'Save failed.');
    } catch (e) {
      setProblem(errOf(e, 'Save failed.'));
    } finally { setSaving(false); }
  };

  const isLocal = /^https?:\/\/(localhost|127\.0\.0\.1|::1|host\.docker\.internal)/i.test(f.base_url || '');

  return (
    <Overlay onCancel={onCancel}>
      <h3 className="mb-4 text-base font-bold text-brand-deep">{f._isNew ? 'Connect an AI provider' : `Edit ${f.display_name}`}</h3>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Code" hint="Short identifier. Cannot be changed later.">
          <input value={f.provider_code} disabled={!f._isNew} onChange={(e) => set('provider_code', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))} placeholder="CLAUDE" className={`${INPUT} disabled:opacity-60`} />
        </Field>
        <Field label="Name">
          <input value={f.display_name} onChange={(e) => set('display_name', e.target.value)} placeholder="Anthropic Claude" className={INPUT} />
        </Field>
      </div>
      <Field label="API style" hint="The wire format, not the brand. Azure OpenAI and self-hosted OpenAI-compatible servers are both 'openai'.">
        <select value={f.api_style} onChange={(e) => set('api_style', e.target.value)} className={INPUT}>
          <option value="anthropic">Anthropic</option>
          <option value="gemini">Google Gemini</option>
          <option value="openai">OpenAI-compatible</option>
        </select>
      </Field>
      <Field label="Endpoint URL" hint="The API base. No key and no model name — those are separate.">
        <input value={f.base_url} onChange={(e) => set('base_url', e.target.value)} placeholder="https://api.anthropic.com/v1" className={INPUT} />
        {f.base_url && !/^https:/i.test(f.base_url) && !isLocal && (
          <p className="mt-1 text-[11px] text-red-600">Use https for a remote endpoint — an API key sent over plain http can be read in transit.</p>
        )}
      </Field>
      <Field label="API key" hint="Encrypted before it is stored, and never shown again — to anyone.">
        {f.key_set && !replacing ? (
          <div className="flex items-center gap-2">
            <span className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
              ••••••••••••{f.api_key_hint || ''} <span className="text-[11px]">stored</span>
            </span>
            <button type="button" onClick={() => setReplacing(true)} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-brand-navy">Replace</button>
          </div>
        ) : (
          <>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="new-password" placeholder="Paste the key from your provider" className={INPUT} />
            {f._preset?.key_help && <p className="mt-1 text-[11px] text-slate-400">{f._preset.key_help}</p>}
            {f.key_set && (
              <button type="button" onClick={() => { setReplacing(false); setApiKey(''); }} className="mt-1 text-[11px] text-slate-500 hover:underline">Keep the existing key instead</button>
            )}
          </>
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Timeout (seconds)">
          <input type="number" min={5} max={600} value={f.timeout_seconds} onChange={(e) => set('timeout_seconds', e.target.value)} className={INPUT} />
        </Field>
        <Field label="Max output tokens">
          <input type="number" min={256} max={128000} value={f.max_output_tokens} onChange={(e) => set('max_output_tokens', e.target.value)} className={INPUT} />
        </Field>
      </div>
      <label className="mt-1 flex cursor-pointer items-start gap-2">
        <input type="checkbox" className="mt-1 h-4 w-4 accent-brand-navy" checked={!!f.is_active} onChange={(e) => set('is_active', e.target.checked)} />
        <span>
          <span className="block text-sm font-semibold text-slate-700">Active</span>
          <span className="block text-[11px] text-slate-500">The assistant calls the chosen model of an active provider. A provider cannot be active without a key.</span>
        </span>
      </label>
      {problem && <Notice tone="danger" Icon={ExclamationTriangleIcon} title="Could not save.">{problem}</Notice>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50">
          {saving ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />} Save
        </button>
      </div>
    </Overlay>
  );
};

const ModelForm = ({ providerCode, preset, existing, hasDefault, onCancel, onSaved }) => {
  const [m, setM] = useState({
    provider_code: providerCode, model_code: '', display_name: '', context_tokens: '',
    is_default: !hasDefault, is_active: true, sort_order: existing.length * 10,
    input_price_per_1k: '', output_price_per_1k: '', price_currency: 'USD',
  });
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const set = (k, v) => setM((x) => ({ ...x, [k]: v }));
  const suggestions = (preset?.models || []).filter((s) => !existing.some((e) => e.model_code === s.model_code));

  const save = async () => {
    setSaving(true); setProblem('');
    try {
      const res = await aiConfigService.saveModel({ ...m, context_tokens: m.context_tokens ? Number(m.context_tokens) : null, sort_order: Number(m.sort_order) || 0 });
      if (res.success) onSaved('Model saved.');
      else setProblem(res.message || 'Save failed.');
    } catch (e) {
      setProblem(errOf(e, 'Save failed.'));
    } finally { setSaving(false); }
  };

  return (
    <Overlay onCancel={onCancel}>
      <h3 className="mb-1 text-base font-bold text-brand-deep">Add a model</h3>
      <p className="mb-4 text-[11px] text-slate-400">for {providerCode}</p>
      {suggestions.length > 0 && (
        <div className="mb-4">
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Suggested</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s.model_code}
                type="button"
                onClick={() => setM((x) => ({ ...x, ...s, context_tokens: s.context_tokens || '', input_price_per_1k: s.input_price_per_1k ?? '', output_price_per_1k: s.output_price_per_1k ?? '' }))}
                className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:border-brand-navy"
              >
                {s.display_name}
              </button>
            ))}
          </div>
        </div>
      )}
      <Field label="Model code" hint="Exactly as the provider's API expects it.">
        <input value={m.model_code} onChange={(e) => set('model_code', e.target.value)} placeholder="claude-opus-5" className={INPUT} />
      </Field>
      <Field label="Name">
        <input value={m.display_name} onChange={(e) => set('display_name', e.target.value)} placeholder="Claude Opus 5" className={INPUT} />
      </Field>
      <Field label="Context window (tokens)" hint="Optional.">
        <input type="number" min={1000} value={m.context_tokens} onChange={(e) => set('context_tokens', e.target.value)} placeholder="1000000" className={INPUT} />
      </Field>
      <div className="mb-4 rounded-lg border border-slate-200 p-3">
        <p className="mb-1 text-sm font-semibold text-slate-700">Pricing (optional)</p>
        <p className="mb-2.5 text-[11px] text-slate-500">Per 1,000 tokens, from the provider's pricing page. Leave blank for a self-hosted model rather than entering zero.</p>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Input / 1k">
            <input type="number" step="0.0001" min="0" value={m.input_price_per_1k} onChange={(e) => set('input_price_per_1k', e.target.value)} className={INPUT} />
          </Field>
          <Field label="Output / 1k">
            <input type="number" step="0.0001" min="0" value={m.output_price_per_1k} onChange={(e) => set('output_price_per_1k', e.target.value)} className={INPUT} />
          </Field>
          <Field label="Currency">
            <input value={m.price_currency} onChange={(e) => set('price_currency', e.target.value.toUpperCase().slice(0, 3))} className={INPUT} />
          </Field>
        </div>
      </div>
      <label className="flex cursor-pointer items-center gap-2">
        <input type="checkbox" className="h-4 w-4 accent-brand-navy" checked={!!m.is_default} onChange={(e) => set('is_default', e.target.checked)} />
        <span className="text-sm text-slate-700">Use this model for the assistant</span>
      </label>
      {problem && <Notice tone="danger" Icon={ExclamationTriangleIcon} title="Could not save.">{problem}</Notice>}
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={save} disabled={saving || !m.model_code.trim() || !m.display_name.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50">
          {saving ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />} Save
        </button>
      </div>
    </Overlay>
  );
};

const Shell = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-brand-deep/50 p-6 backdrop-blur-sm" onMouseDown={onClose}>
    <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-brand-teal" />
          <h2 className="text-lg font-bold text-brand-deep">{title}</h2>
        </div>
        <button onClick={onClose} title="Close" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
          <KeyIcon className="h-3.5 w-3.5" /> API keys are encrypted at rest and never returned.
          <LockClosedIcon className="ml-2 h-3.5 w-3.5" /> The assistant can only run approved actions.
        </span>
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-brand-navy">Close</button>
      </div>
    </div>
  </div>
);

const Overlay = ({ children, onCancel }) => (
  <div className="fixed inset-0 z-[60] grid place-items-center bg-brand-deep/60 p-6" onMouseDown={onCancel}>
    <div className="max-h-[88vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

const INPUT = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40';

const Field = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
    {hint && <p className="mb-1.5 text-[11px] text-slate-500">{hint}</p>}
    {children}
  </div>
);

const TONES = {
  info: 'border-blue-200 bg-blue-50 text-blue-800',
  success: 'border-green-200 bg-green-50 text-green-800',
  danger: 'border-red-200 bg-red-50 text-red-800',
};

const Notice = ({ tone, Icon, title, children }) => (
  <div className={`mb-4 flex items-start gap-2 rounded-lg border px-3 py-2.5 ${TONES[tone]}`}>
    <Icon className="mt-0.5 h-4 w-4 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs font-bold">{title}</p>
      {children && <p className="mt-1 text-[11px] opacity-90">{children}</p>}
    </div>
  </div>
);
